import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { authAPI } from '../services/api';
import { anmeldeSignalGesetzt, impersonationBeenden, impersonationStarten, pruefeSitzung } from '../utils/sitzungsPruefung';

const AuthContext = createContext(null);

// Phase 4c: Tokens leben in HttpOnly-Cookies. User-Daten dürfen weiter
// in localStorage gecached werden (kein Credential).
// getRefreshToken bleibt als no-op exportiert, weil SettingsPage es noch
// destrukturiert für die Session-Listen-Anzeige (Backend ignoriert null
// und liest aus Cookie).
const getRefreshToken = () => null;
const clearUserCache = () => {
  localStorage.removeItem('user');
  // Legacy-Cleanup: alte Token-Reste aus Phase-4-pre-cutover-Builds einmalig
  // aus dem Storage werfen (Browser cached die noch eine Weile).
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('token_admin');
  localStorage.removeItem('user_admin');
};

// Phase 4c Block C: Impersonation läuft jetzt komplett über Cookies.
// Backend setzt beim Start die Impersonation-Cookies (kein Refresh-Cookie =
// Auto-Stop nach 30min), beim Stop fresh Admin-Cookies. Frontend muss nur noch
// das User-Cache neu laden. Helper parkAdminToken/restoreAdminToken entfernt.

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonationInfo, setImpersonationInfo] = useState(null);  // {session_uuid, admin_username, target_username, expires_at, ...}
  // Der letzte vorübergehende Fehler beim Prüfen der Sitzung (Server nicht
  // erreichbar, 429, 5xx). Der Nutzer bleibt dann angemeldet; der
  // VerbindungsHinweis zeigt den Fehler und bietet „Erneut versuchen“ an.
  const [verbindungsFehler, setVerbindungsFehler] = useState(null);

  // Cookies gegen das Backend prüfen und den Nutzer frisch laden. Nur eine
  // echte Ablehnung (401 nach gescheitertem Refresh, 403, 400 „Konto
  // deaktiviert“) beendet die Sitzung; ist der Server nur gerade nicht
  // erreichbar, bleiben Cache und Nutzer stehen und der Fehler wird sichtbar.
  const sitzungPruefen = useCallback(async () => {
    const stand = await pruefeSitzung(() => authAPI.getMe());
    if (stand.art === 'bestaetigt') {
      setUser(stand.nutzer);
      localStorage.setItem('user', JSON.stringify(stand.nutzer));
      setVerbindungsFehler(null);
    } else if (stand.art === 'unerreichbar') {
      console.error('[Auth] Sitzung nicht geprüft — Server vorübergehend nicht erreichbar', stand.fehler);
      setVerbindungsFehler(stand.fehler);
    } else {
      clearUserCache();
      setUser(null);
      setVerbindungsFehler(null);
    }
  }, []);

  // Mount: User-Cache vorab anzeigen, dann via Cookie-Auth gegen Backend
  // validieren (HttpOnly-Cookies werden vom Browser automatisch gesendet).
  useEffect(() => {
    // Ohne Anmelde-Signal gibt es keine Sitzung zu prüfen — ein Gast fragte
    // sonst `/me` und `/refresh` und bekam zweimal 401. Ein Nutzer-Cache ohne
    // Signal stammt aus einer Sitzung, die es nicht mehr gibt.
    if (!anmeldeSignalGesetzt(document.cookie)) {
      clearUserCache();
      setUser(null);
      setLoading(false);
      return;
    }

    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        console.error('[Auth] Nutzer-Cache im localStorage ist kein JSON — verworfen', e);
        localStorage.removeItem('user');
      }
    }

    sitzungPruefen().finally(() => setLoading(false));
  }, [sitzungPruefen]);

  // Login: Backend setzt Cookies (HttpOnly access/refresh + JS-readable CSRF).
  // Frontend cached nur User-Daten (kein Credential).
  const login = useCallback(async (username, password) => {
    const response = await authAPI.login({ username, password });
    // Auth v2: bei offener 2FA/Step-up-Challenge enthält die Antwort KEINEN
    // user, sondern {needs_totp|needs_sms, email, pre_auth_token}. Nur bei
    // echtem Login den User setzen; sonst die Challenge an den Aufrufer geben.
    const userData = response.data?.user;
    if (userData) {
      setUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setVerbindungsFehler(null);
    }
    return response.data;
  }, []);

  // Lädt den User nach abgeschlossener Challenge (Cookies sind dann gesetzt).
  const loadSessionUser = useCallback(async () => {
    const res = await authAPI.getMe();
    setUser(res.data);
    localStorage.setItem('user', JSON.stringify(res.data));
    setVerbindungsFehler(null);
    return res.data;
  }, []);

  // Logout: Backend invalidiert Session + löscht Cookies. Frontend cleart User.
  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (e) {
      // Ignoriere Fehler beim Logout (Backend setzt Cookies trotzdem)
      console.warn('Logout error:', e);
    }

    clearUserCache();
    setUser(null);
    setVerbindungsFehler(null);
  }, []);

  // Logout von allen Geräten
  const logoutAll = useCallback(async () => {
    try {
      await authAPI.logoutAll();
    } catch (e) {
      console.warn('Logout all error:', e);
    }

    clearUserCache();
    setUser(null);
    setVerbindungsFehler(null);
  }, []);

  // User-Daten aktualisieren
  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
      localStorage.setItem('user', JSON.stringify(response.data));
      return response.data;
    } catch (e) {
      console.error('Failed to refresh user:', e);
      return null;
    }
  }, []);

  // E-Mail-Verifizierung erneut senden (anonym, per E-Mail).
  const resendVerification = useCallback(async (email) => {
    return authAPI.resendVerification(email);
  }, []);

  // ========================================================================
  // IMPERSONATION
  // ========================================================================

  // Plattform-Admin schlüpft in einen User.
  // Phase 4c Block C: Backend setzt Impersonation-Cookies (kein Refresh = Auto-Stop
  // nach 30min). Frontend lädt nur User-Cache neu. Scheitert das Laden des
  // Zielkontos, wird die Impersonation zurückgebaut (`impersonationStarten`);
  // gelingt nicht einmal das, steht die Impersonations-Leiste mit „Beenden“ da.
  const startImpersonation = useCallback(async (targetUserId) => {
    const alsInfo = ({ target_user_id, target_username, target_email, session_uuid, expires_at }) => ({
      session_uuid,
      target_user_id,
      target_username,
      target_email,
      expires_at,
      is_impersonating: true,
    });
    const { start, nutzer } = await impersonationStarten({
      starte: () => authAPI.startImpersonation(targetUserId),
      holeNutzer: () => authAPI.getMe(),
      beende: () => authAPI.stopImpersonation(),
      bleibtOffen: (offen) => setImpersonationInfo(alsInfo(offen)),
    });
    setUser(nutzer);
    localStorage.setItem('user', JSON.stringify(nutzer));
    setImpersonationInfo(alsInfo(start));
    return nutzer;
  }, []);

  // Stop: Backend schließt ImpersonationSession + erstellt fresh Admin-Session-Cookies.
  // Frontend lädt nur den User aus /me neu (Cookie ist jetzt wieder Admin).
  // Ist der Server gerade nicht erreichbar, bleibt die Impersonation stehen
  // (die Cookies gehören weiter dem Zielkonto): Leiste bleibt, der Fehler
  // steht im VerbindungsHinweis, und der Aufrufer navigiert nicht weg.
  const stopImpersonation = useCallback(async () => {
    try {
      await impersonationBeenden(() => authAPI.stopImpersonation());
    } catch (e) {
      console.error('[Auth] Impersonation nicht beendet — Server vorübergehend nicht erreichbar', e);
      setVerbindungsFehler(e);
      throw e;
    }
    setImpersonationInfo(null);
    // /me holen — Cookie ist jetzt Admin-Cookie, also kommt Admin-User zurück.
    // Ausgeloggt wird nur, wenn das Cookie wirklich tot ist — nicht, weil der
    // Server gerade nicht antwortet.
    await sitzungPruefen();
  }, [sitzungPruefen]);

  // Der Nutzer, den der Server bestätigt hat. `user` zeigt beim Laden zuerst
  // den Cache aus dem localStorage — das zuletzt angemeldete Konto, das nicht
  // das des Cookies sein muss (Kontowechsel im selben Browser, abgelaufene
  // Sitzung). Zum Anzeigen taugt der Cache, zum Fragen nicht: Eine Abfrage in
  // diesem Moment wäre die eines anderen oder endete in einer 401. Wer beim
  // Mount für die angemeldete Person fragt, hängt sich an `sitzungsNutzer`
  // (Wächter: test_abfragen_nach_bestaetigter_sitzung_canon.py).
  // Konnte der Server die Sitzung gerade nicht prüfen (`verbindungsFehler`),
  // ist sie auch nicht bestätigt: `user` bleibt zum Anzeigen stehen, gefragt
  // wird erst wieder, wenn die Prüfung gelingt.
  const sitzungsNutzer = loading || verbindungsFehler ? null : user;
  const sitzungsNutzerId = sitzungsNutzer?.id ?? null;

  // Impersonation-Status vom Server holen, sobald die Sitzung bestätigt ist.
  useEffect(() => {
    if (sitzungsNutzerId === null) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await authAPI.getImpersonationStatus();
        if (cancelled) return;
        if (res.data?.is_impersonating) {
          setImpersonationInfo(res.data);
        } else {
          setImpersonationInfo(null);
        }
      } catch (e) {
        console.warn('Impersonation-status check failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [sitzungsNutzerId]);

  // Passwort ändern
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await authAPI.changePassword(currentPassword, newPassword);
  }, []);

  // Computed properties
  const isAdmin = user?.is_admin || user?.role === 'super_admin' || user?.role === 'admin';
  const isSuperAdmin = user?.role === 'super_admin';
  const isEmailVerified = user?.email_verified ?? false;

  // Feature-Check
  const canUseFeature = useCallback((feature) => {
    if (!user) return false;
    
    // Admins können alles
    if (isAdmin) return true;
    
    // Aus user-Objekt lesen
    switch (feature) {
      case 'api_import':
        return user.can_api_import ?? false;
      case 'excel_export':
        return user.can_excel_export ?? false;
      case 'leaderboard_participate':
        return user.can_leaderboard_participate ?? false;
      default:
        return false;
    }
  }, [user, isAdmin]);

  // Limit-Check
  const getLimit = useCallback((limitName) => {
    if (!user) return 0;
    
    // Admins haben keine Limits
    if (isAdmin) return -1;
    
    switch (limitName) {
      case 'max_tables':
        return user.max_tables ?? 2;
      case 'simulations_per_day':
        return user.simulations_per_day ?? 5;
      case 'credits_per_month':
        return user.credits_per_month ?? 0;
      default:
        return 0;
    }
  }, [user, isAdmin]);

  const value = {
    user,
    loading,
    sitzungsNutzer,
    login,
    loadSessionUser,
    logout,
    logoutAll,
    refreshUser,
    resendVerification,
    changePassword,
    isAdmin,
    isSuperAdmin,
    isEmailVerified,
    canUseFeature,
    getLimit,
    // Token-Zugriff für spezielle Fälle
    getRefreshToken,
    // Impersonation
    startImpersonation,
    stopImpersonation,
    impersonationInfo,
    isImpersonating: !!impersonationInfo?.is_impersonating,
    // Vorübergehender Verbindungsfehler (Nutzer bleibt angemeldet)
    verbindungsFehler,
    sitzungPruefen,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
